import React, { memo } from "react";
import { useTranslation } from "react-i18next";

import Slider from "react-slick";
import classNames from "classnames";
import { getCurrentCulture, ISOToDisplayCode } from "@utils";
import { ReactLazyImportWithGuard as lazy } from "@lib/lazy-import-with-guard";
import PromoContentImage from "@components/icons/PromoContentImage";
import { PromoOfferType } from "@utils/common/promoOfferTypeEnum";
import ErrorBoundaryWithPropagation from "@components/boundary/ErrorBoundaryWithPropagation";
import { THEME_SETTINGS } from "@theme-settings";

const loadFailPath = `/${getCurrentCulture()}/app-update`;

// prettier-ignore
const ReactMarkdown = lazy(loadFailPath, () => import("react-markdown"));

export default memo(function CasinoOnBoardingSlider(props: {
	isLrg?: boolean;
	hasOnboardingStyling?: boolean;
}) {
	const { t } = useTranslation();

	const settings = {
		arrows: false,
		dots: true,
		infinite: true,
		speed: 300,
		variableWidth: true,
		autoplay: true,
		slideToShow: 1,
		autoplaySpeed: 8000,
	};

	const titleClasses = classNames("card--membership__intro", {
		"card--membership__intro--med": getCurrentCulture() == "sq",
	});

	if (props.hasOnboardingStyling) {
		return (
			<div className="card--membership__banner">
				<div className="card--membership__content">
					<ReactMarkdown className={titleClasses}>
						{t("WELCOME_BONUS.ANNOUNCEMENT", {
							amount: THEME_SETTINGS.welcomeBonusAmount,
							currency: ISOToDisplayCode(
								THEME_SETTINGS.defaultCurrency
							),
						})}
					</ReactMarkdown>
					<div className="card--membership__desc">
						{t("WELCOME_BONUS.DESCRIPTION", {
							amount: THEME_SETTINGS.welcomeBonusAmount,
							currency: ISOToDisplayCode(
								THEME_SETTINGS.defaultCurrency
							),
						})}
					</div>
				</div>
				<img
					className="card--membership__media"
					src="/assets/images/bg/casino-mobile--light.png"
					alt=""
				/>
			</div>
		);
	}

	return (
		<ErrorBoundaryWithPropagation
			componentName={"Mobile/OnBoardingSlider.tsx"}
		>
			<div className="section section--promo">
				<div className="section__body">
					<Slider
						{...settings}
						className="carousel carousel--ads carousel--onboarding"
					>
						<div className="carousel__item carousel--ads__item">
							<div className="carousel__slide carousel--ads__slide">
								<div className="carousel__media">
									<PromoContentImage
										page={PromoOfferType.MEMBERSHIP}
										wrapperClassName="carousel__picture"
										className="carousel__img"
										image="login-1.png"
										fallback="membership-mobile.png"
									/>
								</div>
								<div className="carousel__content carousel__content carousel--onboarding__content">
									<ReactMarkdown className="carousel__title carousel--onboarding__title">
										{t("WELCOME_BONUS.ENCOURAGEMENT", {
											amount: THEME_SETTINGS.welcomeBonusAmount,
											currency: ISOToDisplayCode(
												THEME_SETTINGS.defaultCurrency
											),
										})}
									</ReactMarkdown>
									<div className="carousel__desc carousel__desc carousel--onboarding__desc">
										{t("COMMON.SLIDER.WAGERING_EXCLUSION")}
									</div>
								</div>
							</div>
						</div>

						<div className="carousel__item carousel--ads__item">
							<div className="carousel__slide carousel--ads__slide">
								<div className="carousel__media">
									<PromoContentImage
										page={PromoOfferType.MEMBERSHIP}
										wrapperClassName="carousel__picture"
										className="carousel__img"
										image="login-2.png"
										fallback="membership-mobile.png"
									/>
								</div>
								<div className="carousel__content carousel--onboarding__content">
									<ReactMarkdown className="carousel__title carousel--onboarding__title">
										{t("COMMON.SLIDER.TIPWIN_SERVICES")}
									</ReactMarkdown>
									<div className="carousel__desc carousel--onboarding__desc">
										{t("COMMON.SLIDER.WAGERING_EXCLUSION")}
									</div>
								</div>
							</div>
						</div>

						<div className="carousel__item carousel--ads__item">
							<div className="carousel__slide carousel--ads__slide">
								<div className="carousel--onboarding__media carousel--onboarding__media--welcome">
									<div className="carousel__media">
										<PromoContentImage
											page={PromoOfferType.MEMBERSHIP}
											wrapperClassName="carousel__picture"
											className="carousel__img"
											image="login-3.png"
											fallback="membership-mobile.png"
										/>
									</div>
								</div>
								<div className="carousel__content carousel--onboarding__content">
									<ReactMarkdown className="carousel__title carousel--onboarding__title">
										{t("COMMON.SLIDER.WELCOME")}
									</ReactMarkdown>
									<div className="carousel__desc carousel--onboarding__desc">
										{t("COMMON.SLIDER.WIDE_OFFER")}
									</div>
								</div>
							</div>
						</div>
					</Slider>
				</div>
			</div>
		</ErrorBoundaryWithPropagation>
	);
});
